/**
 * FE-15: Funnel Tab — Activation / Content Loop / Monetization funnels.
 * Horizontal bar chart with CSS width percentage for funnel visualization.
 */

import { useCallback, useEffect, useState } from "react";
import { fetchFunnelMetrics } from "../../api/admin-metrics";
import type { FunnelResult, FunnelStep } from "../../api/admin-metrics";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { defaultDateRange, fmt, pct } from "./helpers";

const FUNNELS = ["activation", "content_loop", "monetization"] as const;
type FunnelName = (typeof FUNNELS)[number];

const FUNNEL_LABEL_KEYS: Record<FunnelName, string> = {
  activation: "funnel.activation",
  content_loop: "funnel.content_loop",
  monetization: "funnel.monetization",
};

export function FunnelTab({
  highlightStep,
}: {
  highlightStep?: string;
}): React.JSX.Element {
  const [funnel, setFunnel] = useState<FunnelName>("activation");
  const { startDate, endDate, setStartDate, setEndDate } = useDateRange();
  const { data, error, loading, load } = useFunnelData();

  useEffect(() => {
    void load(funnel, startDate, endDate);
  }, [load, funnel, startDate, endDate]);

  return (
    <div>
      <Controls
        funnel={funnel}
        startDate={startDate}
        endDate={endDate}
        loading={loading}
        onFunnelChange={setFunnel}
        onStartChange={setStartDate}
        onEndChange={setEndDate}
      />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && !data && (
        <p style={shared.loadingText}>{t("common.loading")}</p>
      )}
      {data && <FunnelChart steps={data.steps} highlightStep={highlightStep} />}
    </div>
  );
}

// ─── Funnel Chart ───

function FunnelChart({
  steps,
  highlightStep,
}: {
  steps: ReadonlyArray<FunnelStep>;
  highlightStep?: string;
}): React.JSX.Element {
  const max = steps.length > 0 ? steps[0].users : 1;
  return (
    <section style={shared.section}>
      <h3 style={shared.sectionTitle}>{t("funnel.funnel_steps")}</h3>
      <div style={s.funnelContainer}>
        {steps.map((step, i) => (
          <FunnelRow
            key={step.name}
            step={step}
            max={max}
            index={i}
            highlighted={step.name === highlightStep}
          />
        ))}
        {steps.length === 0 && (
          <p style={shared.loadingText}>{t("funnel.no_funnel_data")}</p>
        )}
      </div>
    </section>
  );
}

function FunnelRow({
  step,
  max,
  index,
  highlighted,
}: {
  step: FunnelStep;
  max: number;
  index: number;
  highlighted?: boolean;
}): React.JSX.Element {
  const widthPct = max > 0 ? (step.users / max) * 100 : 0;
  return (
    <div
      style={{
        ...s.funnelStep,
        ...(highlighted ? { background: "#eff6ff", borderRadius: 6 } : {}),
      }}
    >
      <div style={s.funnelLabel}>
        <span style={s.stepIndex}>{index + 1}</span>
        <span style={s.stepName}>{step.name}</span>
      </div>
      <div style={s.barTrack}>
        <div
          style={{
            ...s.barFill,
            width: `${Math.max(widthPct, 2)}%`,
          }}
        />
      </div>
      <div style={s.funnelStats}>
        <span style={s.userCount}>{fmt(step.users)}</span>
        <span style={s.convRate}>{pct(step.rate)}</span>
      </div>
    </div>
  );
}

// ─── Controls ───

function Controls({
  funnel,
  startDate,
  endDate,
  loading,
  onFunnelChange,
  onStartChange,
  onEndChange,
}: {
  funnel: FunnelName;
  startDate: string;
  endDate: string;
  loading: boolean;
  onFunnelChange: (f: FunnelName) => void;
  onStartChange: (v: string) => void;
  onEndChange: (v: string) => void;
}): React.JSX.Element {
  return (
    <div style={s.controlRow}>
      <label style={s.label}>
        {t("funnel.label_funnel")}
        <select
          value={funnel}
          onChange={(e) => onFunnelChange(e.target.value as FunnelName)}
          style={s.select}
        >
          {FUNNELS.map((f) => (
            <option key={f} value={f}>
              {t(FUNNEL_LABEL_KEYS[f])}
            </option>
          ))}
        </select>
      </label>
      <label style={s.label}>
        {t("common.from")}
        <input
          type="date"
          value={startDate}
          onChange={(e) => onStartChange(e.target.value)}
          style={s.dateInput}
        />
      </label>
      <label style={s.label}>
        {t("common.to")}
        <input
          type="date"
          value={endDate}
          onChange={(e) => onEndChange(e.target.value)}
          style={s.dateInput}
        />
      </label>
      {loading && <span style={shared.loadingText}>{t("common.loading")}</span>}
    </div>
  );
}

// ─── Hooks ───

function useDateRange(): {
  startDate: string;
  endDate: string;
  setStartDate: (v: string) => void;
  setEndDate: (v: string) => void;
} {
  const defaults = defaultDateRange(30);
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  return { startDate, endDate, setStartDate, setEndDate };
}

function useFunnelData(): {
  data: FunnelResult | null;
  error: string | null;
  loading: boolean;
  load: (funnel: string, start: string, end: string) => Promise<void>;
} {
  const [data, setData] = useState<FunnelResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (funnel: string, start: string, end: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        setData(
          await fetchFunnelMetrics({
            funnel,
            start_date: start,
            end_date: end,
          }),
        );
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : t("common.failed_to_load"),
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, error, loading, load };
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  controlRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: 12,
    marginBottom: 24,
    flexWrap: "wrap",
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  select: {
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
    minWidth: 160,
  },
  dateInput: {
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
  },
  funnelContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  funnelStep: { display: "flex", alignItems: "center", gap: 12 },
  funnelLabel: {
    width: 180,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  stepIndex: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "var(--color-bg-hover)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    flexShrink: 0,
  },
  stepName: { fontSize: 13, fontWeight: 500 },
  barTrack: {
    flex: 1,
    height: 28,
    background: "var(--color-bg-secondary)",
    borderRadius: 4,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    background: "var(--color-primary)",
    borderRadius: 4,
    transition: "width 0.3s ease",
  },
  funnelStats: {
    width: 120,
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexShrink: 0,
  },
  userCount: {
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "var(--font-mono)",
  },
  convRate: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
  },
};
